import {html} from 'lit';
import {ElementUtil} from '../../utils/elementUtil.js';
import {BaseComponent} from '../basecomponent.js';

import Quill from 'quill';
// Inline into the editor tree so styles work inside Vaadin dialog overlay shadow DOM.
import quillSnowCss from 'quill/dist/quill.snow.css?raw';

/* 
 * Rich text field (Quill) for use inside dl-form-panel via setCustomValue / getCustomValue.
 */

/* global customElements */
export class RichText extends BaseComponent {
	
	static get properties() {
		return {
			label: {type: String},
			name: {type: String},
			required: {type: Boolean},
			value: {type: String, attribute: true},
			editorheight: {type: String} 
		};
	}
		
	constructor() {
		super();	
		
		this.value = '';
		this.editorheight = '200px';
		this.quill = null;
		this.initialText = '';
		this.editorReady = false;
		this.htmlSourceMode = false;
		this.htmlSourceArea = null;

		var richText = this;
		setTimeout(function(){
			richText.init();
		});
	}
	
	render() {
		return html`	
			<slot></slot>		
		`;
	}	
	
	init() {
		var richText = this;
		
		richText.editorId = richText.name + 'Editor';
		
		richText.customField = ElementUtil.create({
			tag: 'vaadin-custom-field',
			label: richText.label,
			style: 'width: ' + richText.style.width + 
							'; margin-left: ' +	richText.style['margin-left'] + 
							'; margin-right: ' + richText.style['margin-right'] +
							'; margin-top: -50px; display: block; --lumo-text-field-size: 12px;',
			name: richText.name,
			required: richText.required,
			value: richText.value,
			children: [
				{
					tag: 'div',
					id: richText.editorId,
					itemid: 'editor',
					style: 'width: 100%;'
				}
			]
		});

		richText.append(richText.customField);
		// Style root may only be correct after this node is in the dialog tree.
		richText.ensureSnowStyles();
		setTimeout(function() {
			richText.ensureSnowStyles();
		}, 0);

		var editorHost = ElementUtil.query(richText.customField, 'editor');
		editorHost.style.minHeight = richText.editorheight;

		var toggleInlineFormat = function(formatName) {
			this.quill.focus();
			var formats = this.quill.getFormat();
			this.quill.format(formatName, !formats[formatName], Quill.sources.USER);
		};

		richText.quill = new Quill(editorHost, {
			theme: 'snow',
			modules: {
				toolbar: {
					container: [
						[{ header: [1, 2, 3, false] }],
						['bold', 'italic', 'underline'],
						[{ align: [] }],
						[{ list: 'ordered' }, { list: 'bullet' }],
						[{ indent: '-1' }, { indent: '+1' }],
						['link', 'image'],
						[{ color: [] }, { background: [] }],
						['clean', 'html']
					],
					handlers: {
						// Toggle from actual selection format — ql-active is unreliable in dialog overlays.
						bold: function() {
							toggleInlineFormat.call(this, 'bold');
						},
						italic: function() {
							toggleInlineFormat.call(this, 'italic');
						},
						underline: function() {
							toggleInlineFormat.call(this, 'underline');
						},
						html: function() {
							richText.toggleHtmlSource();
						}
					}
				}
			}
		});

		var editorRoot = richText.quill.root;
		editorRoot.style.minHeight = richText.editorheight;

		richText.applyToolbarTitles();
		richText.setupHtmlSourceButton();

		// Keep editor selection when clicking toolbar buttons/pickers.
		var toolbarModule = richText.quill.getModule('toolbar');
		if (toolbarModule && toolbarModule.container) {
			toolbarModule.container.addEventListener('mousedown', function(event) {
				event.preventDefault();
			});
		}

		// Dialogs can suppress pointer events on overlays; restore for Quill pickers/tooltips.
		setTimeout(function() {
			var toolbar = richText.querySelector('.ql-toolbar');
			if (toolbar) {
				toolbar.style.pointerEvents = 'all';
			}
			var pickers = richText.querySelectorAll('.ql-picker-options, .ql-tooltip');
			for (var i = 0; i < pickers.length; i++) {
				pickers[i].style.pointerEvents = 'all';
				pickers[i].style.zIndex = '10000';
			}
		}, 100);

		richText.editorReady = true;
		if (richText.initialText) {
			richText.applyHtml(richText.initialText);
		} else if (richText.value) {
			richText.applyHtml(richText.value);
		}

		richText.quill.on('text-change', function() {
			richText.customField.value = richText.readHtml();
		});
	}

	/**
	 * Quill snow CSS must apply inside Vaadin dialog overlay shadow roots.
	 * Document-injected webpack styles do not cross that boundary (native ul/ol
	 * still show markers; Quill's data-list / ql-indent rules would not).
	 */
	ensureSnowStyles() {
		var root = this.getRootNode();
		if (!root) {
			return;
		}

		if (typeof CSSStyleSheet !== 'undefined' && root.adoptedStyleSheets) {
			if (root._drivelineQuillSnowAdopted) {
				return;
			}
			try {
				var sheet = new CSSStyleSheet();
				sheet.replaceSync(quillSnowCss);
				root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
				root._drivelineQuillSnowAdopted = true;
				return;
			} catch (ignore) {
				// Fall through to style-tag injection.
			}
		}

		var styleParent = (root === document || root === document.body) ? document.head : root;
		if (styleParent.querySelector && styleParent.querySelector('style[data-quill-snow]')) {
			return;
		}
		var styleElement = document.createElement('style');
		styleElement.setAttribute('data-quill-snow', 'true');
		styleElement.textContent = quillSnowCss;
		styleParent.appendChild(styleElement);
	}

	setupHtmlSourceButton() {
		var richText = this;
		var toolbar = richText.quill && richText.quill.getModule('toolbar');
		if (!toolbar || !toolbar.container) {
			return;
		}
		var htmlButton = toolbar.container.querySelector('button.ql-html');
		if (!htmlButton) {
			return;
		}
		// Quill has no built-in html icon; reuse the code icon glyph.
		htmlButton.innerHTML = '<svg viewbox="0 0 18 18"><polyline class="ql-even ql-stroke" points="5 7 3 9 5 11"/><polyline class="ql-even ql-stroke" points="13 7 15 9 13 11"/><line class="ql-stroke" x1="10" x2="8" y1="5" y2="13"/></svg>';
	}

	toggleHtmlSource() {
		var richText = this;
		if (!richText.quill) {
			return;
		}

		var toolbar = richText.quill.getModule('toolbar');
		var htmlButton = toolbar && toolbar.container
			? toolbar.container.querySelector('button.ql-html')
			: null;
		var editorContainer = richText.quill.container;

		if (richText.htmlSourceMode) {
			var editedHtml = richText.htmlSourceArea ? richText.htmlSourceArea.value : '';
			richText.applyHtml(editedHtml);
			if (richText.htmlSourceArea) {
				richText.htmlSourceArea.style.display = 'none';
			}
			editorContainer.style.display = '';
			richText.htmlSourceMode = false;
			if (htmlButton) {
				htmlButton.classList.remove('ql-active');
			}
			return;
		}

		if (!richText.htmlSourceArea) {
			richText.htmlSourceArea = document.createElement('textarea');
			richText.htmlSourceArea.setAttribute('itemid', 'html-source');
			richText.htmlSourceArea.style.cssText = 'display: none; width: 100%; box-sizing: border-box; '
				+ 'min-height: ' + richText.editorheight + '; font-family: Consolas, Monaco, monospace; '
				+ 'font-size: 13px; line-height: 1.4; padding: 8px; border: 1px solid #ccc; resize: vertical;';
			editorContainer.parentNode.insertBefore(richText.htmlSourceArea, editorContainer.nextSibling);
		}

		richText.htmlSourceArea.value = richText.readHtml();
		richText.htmlSourceArea.style.display = 'block';
		editorContainer.style.display = 'none';
		richText.htmlSourceMode = true;
		if (htmlButton) {
			htmlButton.classList.add('ql-active');
		}
		richText.htmlSourceArea.focus();
	}

	applyToolbarTitles() {
		var richText = this;
		if (!richText.quill) {
			return;
		}
		var toolbar = richText.quill.getModule('toolbar');
		if (!toolbar || !toolbar.container) {
			return;
		}

		var titles = {
			bold: 'Bold',
			italic: 'Italic',
			underline: 'Underline',
			link: 'Insert link',
			image: 'Insert image',
			clean: 'Clear formatting',
			html: 'Edit HTML source',
			'list-ordered': 'Numbered list',
			'list-bullet': 'Bullet list',
			'indent-+1': 'Increase indent',
			'indent--1': 'Decrease indent',
			header: 'Heading',
			align: 'Align',
			color: 'Text color',
			background: 'Background color'
		};

		var setTitle = function(element, title) {
			if (!element || !title) {
				return;
			}
			element.setAttribute('title', title);
			element.setAttribute('aria-label', title);
		};

		var resolveFormat = function(element) {
			var classList = element.classList;
			for (var index = 0; index < classList.length; index++) {
				var className = classList[index];
				if (className.indexOf('ql-') === 0 && className !== 'ql-picker'
						&& className !== 'ql-picker-label' && className !== 'ql-picker-options'
						&& className !== 'ql-active' && className !== 'ql-expanded') {
					return className.substring(3);
				}
			}
			return null;
		};

		var buttons = toolbar.container.querySelectorAll('button');
		for (var buttonIndex = 0; buttonIndex < buttons.length; buttonIndex++) {
			var button = buttons[buttonIndex];
			var format = resolveFormat(button);
			if (!format) {
				continue;
			}
			var value = button.getAttribute('value');
			var titleKey = (value != null && value !== '') ? (format + '-' + value) : format;
			setTitle(button, titles[titleKey] || titles[format]);
		}

		var pickers = toolbar.container.querySelectorAll('.ql-picker');
		for (var pickerIndex = 0; pickerIndex < pickers.length; pickerIndex++) {
			var picker = pickers[pickerIndex];
			var pickerFormat = resolveFormat(picker);
			var pickerTitle = titles[pickerFormat];
			setTitle(picker, pickerTitle);
			setTitle(picker.querySelector('.ql-picker-label'), pickerTitle);
		}
	}

	applyHtml(html) {
		var richText = this;
		if (!richText.quill) {
			return;
		}
		var content = html || '';
		richText.quill.setText('');
		if (content) {
			richText.quill.clipboard.dangerouslyPasteHTML(0, content, 'silent');
		}
		richText.customField.value = richText.readHtml();
	}

	readHtml() {
		var richText = this;
		if (!richText.quill) {
			return '';
		}
		if (typeof richText.quill.getSemanticHTML === 'function') {
			return richText.quill.getSemanticHTML();
		}
		return richText.quill.root.innerHTML;
	}
	
	setCustomValue(newValue) {
		var richText = this;
		
		richText.initialText = newValue;
		if (richText.editorReady) {
			richText.applyHtml(newValue);
		}
	}
	
	updateValue(newValue) {
		var richText = this;
		setTimeout(function(){
			richText.applyHtml(newValue);
		}, 500);		
	}
	
	getCustomValue() {
		var richText = this;		
		
		if (richText.htmlSourceMode && richText.htmlSourceArea) {
			richText.applyHtml(richText.htmlSourceArea.value);
			richText.htmlSourceArea.style.display = 'none';
			if (richText.quill && richText.quill.container) {
				richText.quill.container.style.display = '';
			}
			richText.htmlSourceMode = false;
			var toolbar = richText.quill && richText.quill.getModule('toolbar');
			var htmlButton = toolbar && toolbar.container
				? toolbar.container.querySelector('button.ql-html')
				: null;
			if (htmlButton) {
				htmlButton.classList.remove('ql-active');
			}
		}

		richText.customField.value = richText.readHtml();
		return richText.customField.value;
	}	
	
	validate() {
		var richText = this;

		if (richText.htmlSourceMode && richText.htmlSourceArea) {
			var sourceText = richText.htmlSourceArea.value || '';
			return sourceText.replace(/<[^>]*>/g, '').trim().length > 0;
		}
		
		if (!richText.quill) {
			return false;
		}
		return richText.quill.getText().trim().length > 0;
	}

	disconnectedCallback() {
		var richText = this;	
		
		if (richText.quill) {
			richText.quill.off('text-change');
			richText.quill = null;
			richText.editorReady = false;
		}
		super.disconnectedCallback();
	}
		
};
customElements.define('dl-rich-text', RichText);
